import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  ViewContainerRef,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CARER_ROLE, COORDINATOR_ROLE, CUSTOMER_CARER_ROLE } from '@careapp-group/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { MultiCustomerContentCreation } from '../customer-content-creation/customer-content-creation';
import { TentativePermissionRequestDto } from '@careapp-group/ng-sdk';

@Component({
    selector: 'ca-invite',
    templateUrl: 'invite.component.html',
    styleUrls: ['./invite.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InviteComponent extends MultiCustomerContentCreation implements OnDestroy {
  protected _disabled = false;
  protected _loading = false;
  protected _roleOptions = [];

  readonly MAX_AVATAR_COUNT = 3;

  get customerListIds(): string[] {
    return this.form.get('customerListIds').value;
  }
  set customerListIds(customerListIds: string[]) {
    this.form.patchValue({
      customerListIds,
    });
    this.changeDetector.detectChanges();
  }

  get customerIds() {
    return this.form.get('customerIds').value;
  }

  set customerIds(customerIds: string[]) {
    this.form.patchValue({
      customerIds,
    });
    this.changeDetector.detectChanges();
  }

  CARER = CARER_ROLE;
  COORDINATOR_ROLE = COORDINATOR_ROLE;

  email = new UntypedFormControl('', Validators.compose([Validators.required, Validators.email]));
  customerIdsControl = new UntypedFormControl([]);
  customerListIdsControl = new UntypedFormControl([]);
  externalId = new UntypedFormControl('', []);
  role = new UntypedFormControl(
    '',
    Validators.compose([Validators.required, this.validRoleType.bind(this)])
  );

  form: UntypedFormGroup;

  @Input()
  set disabled(val: boolean) {
    this._disabled = val;
    if (this._disabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
  get disabled() {
    return this._disabled;
  }

  @Input()
  set loading(val: boolean) {
    this._loading = val;
    if (this._loading && !this.form.disabled) {
      this.form.disable();
    } else if (!this._loading && !this._disabled && this.form.disabled) {
      this.form.enable();
    }
  }
  get loading() {
    return this._loading;
  }

  @Input()
  set roleOptions(val: string[]) {
    this._roleOptions = val;
  }
  get roleOptions() {
    return this._roleOptions;
  }

  @Input() error: string;
  @Output() invite = new EventEmitter();

  private roleSub: Subscription;

  constructor(
    public fb: UntypedFormBuilder,
    private changeDetector: ChangeDetectorRef,
    protected store: Store<any>,
    protected dialog: MatDialog,
    protected viewContainerRef: ViewContainerRef
  ) {
    super(dialog, store, viewContainerRef);

    this.form = this.createForm();

    // By default, coordinators oversee all customers
    // By default, carers only see assigned customers
    // this patches the qualifications when the role changes
    this.roleSub = this.role.valueChanges.subscribe((value) => {
      if (value === COORDINATOR_ROLE) {
        this.form.patchValue({
          qualifications: {
            accessAllCustomers: true,
          },
        });
      } else if (value === CARER_ROLE) {
        this.form.patchValue({
          qualifications: {
            accessAllCustomers: false,
          },
        });
      } else {
        this.form.patchValue({
          qualifications: {},
        });
      }
    });
  }

  ngOnDestroy() {
    this.roleSub.unsubscribe();
  }

  createForm() {
    return this.fb.group(
      {
        email: this.email,
        role: this.role,
        qualifications: this.fb.group({
          accessAllCustomers: [false],
        }),
        externalId: this.externalId,
        customerIds: this.customerIdsControl,
        customerListIds: this.customerListIdsControl,
      }
      // :Note: (T.B. 5 Sep '23) Uncomment to require selected customers for limited carer invites.
      // { validators: Validators.compose([this.arrayNotEmptyWhenLimitedCarerValidator.call(this)]) }
    );
  }

  submit(form: {
    email: string;
    role: string;
    qualifications: any;
    customerIds: string[];
    customerListIds: string[];
  }) {
    if (this.form.invalid) {
      return false;
    }
    if (this.loading) {
      return false;
    }
    if (this.disabled) {
      return false;
    }
    const payload = { ...form, permissions: <TentativePermissionRequestDto[]>[] };

    if (payload.role !== CARER_ROLE) {
      // Currently carers are the only roles that support qualifications
      // so we clear out any if that role changes to avoid confusion.
      payload.qualifications = {};
    }

    // Convert selected limited carer customers into permission requests.
    if (
      payload.role !== CARER_ROLE ||
      (payload.role === CARER_ROLE && !!payload.qualifications?.accessAllCustomers)
    ) {
      delete payload.customerIds;
    } else {
      const permissions = (payload.customerIds ?? []).map(
        (id) =>
          ({
            modelId: id,
            modelName: 'Customer',
            role: CUSTOMER_CARER_ROLE,
          } as TentativePermissionRequestDto)
      );
      payload.permissions = permissions;
      delete payload.customerIds;
    }

    delete payload.customerListIds;

    this.invite.emit(payload);
    this.form.reset();
    return false;
  }

  validRoleType(control: UntypedFormControl) {
    if (this.roleOptions.includes(control.value)) {
      return null;
    }
    return { invalidRoleType: true };
  }

  arrayNotEmptyWhenLimitedCarerValidator(form: UntypedFormGroup): ValidatorFn {
    return (group: UntypedFormGroup): ValidationErrors => {
      const idsControl = group.controls['customerIds'];
      const isCarer = group.controls['role'].value === CARER_ROLE;

      if (isCarer) {
        const isLimitedCarer = !group.controls['qualifications']?.value['accessAllCustomers'];
        const hasIds = !!(group.controls['customerIds']?.value ?? []).length;

        if (isLimitedCarer && !hasIds) {
          idsControl.setErrors({ required: true });
          return;
        }
      }
      idsControl.setErrors(null);
      return;
    };
  }
}
